import React, { createContext, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import services from "services";
import { useDispatch } from "react-redux";
import { AxiosResponse, InternalAxiosRequestConfig } from "axios";

import pathnames from "routes/pathnames";

import sanitizeError from "common/sanitize-error";

import CONSTANT from "constants/constant";

import BrowserRoutersDto from "dto/routes/routes-dto";
import ErrorResponseDto from "dto/services/error-response-dto";
import { InterceptorContextDto } from "dto/context/interceptor-provider-dto";

import { resetProfile } from "store/slices/profile-slice";

const sharedStyles = "margin: 5px 0; padding: 8px; border-radius: 20px; color: #FFFFFF;";
const initStyles = `${sharedStyles} background: #673AB7;`;
const ejectStyles = `${sharedStyles} background: #ffb127;`;
const reqDataStyles = `${sharedStyles} background: #F8EA8C;`;
const reqParamsStyles = `${sharedStyles} background: #ff9514;`;
const respSuccessStyles = `${sharedStyles} background: #32ac97;`;
const errorStyles = `${sharedStyles} background: #F79489;`;

export const AxiosContext = createContext<InterceptorContextDto>({ onHandleCancelRequest: () => {} });

const interceptorDebug = (title: string, styles: string, data?: any) => {
	if (!process.env.NODE_ENV) return;
	console.log(`%c ${title}`, styles);

	if (data) console.log(data);
	console.log("");
};

const InterceptorProvider = ({ children, routes }: { children: React.ReactElement; routes: BrowserRoutersDto }) => {
	const controller = useRef<Map<string, AbortController>>(new Map()).current;
	const dispatch = useDispatch();
	const [initialed, setInitialed] = useState(false);
	const requestInterceptor = useRef<number | null>(null);
	const responseInterceptor = useRef<number | null>(null);

	//prettier-ignore
	const onHandleForceLogout = useCallback(async (error: ErrorResponseDto, path?: string) => {
		localStorage.clear();
		
		sessionStorage.clear();

		dispatch(resetProfile());
		
		routes.navigate(path ?? pathnames.articles);

		dispatch({ type: CONSTANT.REDUX_ACTION.LOGOUT });

		return Promise.reject(error);
	}, [routes, dispatch]);

	const onHandleRequest = useCallback((config: InternalAxiosRequestConfig) => {
		switch (config.method) {
			case "get": {
				interceptorDebug(`GET REQUESTING 🚀 ${config.baseURL! + config.url}`, reqParamsStyles, config.params);
				break;
			}
			case "put": {
				interceptorDebug(`PUT REQUESTING 🚀 ${config.baseURL! + config.url}`, reqDataStyles, config.data);
				break;
			}
			case "delete": {
				interceptorDebug(`DELETE REQUESTING 🚀 ${config.baseURL! + config.url}`, reqDataStyles, config.data);
				break;
			}
			default: {
				interceptorDebug(`POST REQUESTING 🚀 ${config.baseURL! + config.url}`, reqDataStyles, config.data);
				break;
			}
		}

		const apiKey = localStorage.getItem(CONSTANT.API_KEY) ?? sessionStorage.getItem(CONSTANT.API_KEY);

		if (apiKey) config.headers["x-api-key"] = apiKey;

		return config;
	}, []);

	//prettier-ignore
	const onHandleRequestError = useCallback((error: ErrorResponseDto): Promise<ErrorResponseDto> => {
		interceptorDebug("REQUESTING ERROR 👎", errorStyles, sanitizeError(error));

		controller.delete(error.config!.url!);

		return Promise.reject(error);
	}, [controller]);

	//prettier-ignore
	const onHandleResponse = useCallback((response: AxiosResponse): AxiosResponse => {
		interceptorDebug(`RESPONSE SUCCESS: 🌟 ${response.config.baseURL! + response.config.url}`, respSuccessStyles, response.data);

		controller.delete(response.config.url!);

		return response;
	}, [controller]);

	//prettier-ignore
	const onResponseError = useCallback(async (error: ErrorResponseDto): Promise<ErrorResponseDto>  => {
		interceptorDebug(`RESPONSE ERROR 🥲 ${error.config!.baseURL! + error.config!.url}`, errorStyles, error);

		controller.delete(error.config!.url!);
		
		const status  = error.response?.status;

		if(status === 403) onHandleForceLogout(error);

		return Promise.reject(error);
	}, [controller, onHandleForceLogout]);

	const onHandleEjectAxiosInterceptor = useCallback(() => {
		if (requestInterceptor.current !== null) {
			interceptorDebug("Ejected Request Axios Interceptor! 👋", ejectStyles);

			services.interceptors.request.eject(requestInterceptor.current);
		}
		if (responseInterceptor.current !== null) {
			interceptorDebug("Ejected Response Axios Interceptor! 👋", ejectStyles);

			services.interceptors.response.eject(responseInterceptor.current);
		}
	}, []);

	//prettier-ignore
	const onHandleCancelRequest = useCallback((cancelId: string) => {
		const cancelIdController = controller.get(cancelId);

		if (cancelIdController) {
			cancelIdController.abort();

			controller.delete(cancelId);
		}
	}, [controller]);

	useEffect(() => {
		interceptorDebug(`Init Axios Interceptor! 🎉`, initStyles);

		requestInterceptor.current = services.interceptors.request.use(onHandleRequest, onHandleRequestError, { synchronous: true });

		responseInterceptor.current = services.interceptors.response.use(onHandleResponse, onResponseError, { synchronous: true });

		setInitialed(true);
	}, [onHandleRequest, onHandleRequestError, onHandleResponse, onResponseError]);

	useEffect(() => {
		return () => {
			onHandleEjectAxiosInterceptor();
		};
	}, [onHandleEjectAxiosInterceptor]);

	const contexts = useMemo(() => ({ onHandleCancelRequest }), [onHandleCancelRequest]);

	if (!initialed) return null;

	return <AxiosContext.Provider value={contexts}>{children}</AxiosContext.Provider>;
};

export default memo(InterceptorProvider);
